import { disconnectConnection, listConnections, type Connection } from "../lib/auth.js";
import { el } from "../lib/dom.js";
import { messageOf, type Shell } from "./ui.js";

/**
 * Connected apps: every OAuth connection the account holds, and Disconnect for
 * each. One connection is one grant, so connecting Hermes twice shows twice,
 * told apart by when each was made, and disconnecting one leaves the other.
 *
 * A section of the Settings page (g48), where it was a popup of its own (g47).
 * Like the email change, it goes through `lib/auth.ts`, the auth surface a
 * screen may call; list data never comes this way. It loads when the page
 * mounts, and a failed load is a message and Try again inside the section: the
 * rest of Settings, theme included, is usable offline.
 */

interface Host {
  confirm: Shell["confirm"];
  announce(message: string): void;
}

export interface ConnectionsSection {
  node: HTMLElement;
  /** True while a disconnect is in flight. */
  busy(): boolean;
  dispose(): void;
}

const day = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", year: "numeric" });

function describe(connection: Connection): string {
  const made = `Connected ${day.format(connection.createdAt)}`;
  const used = connection.lastUsedAt === null ? "Never used" : `Last used ${day.format(connection.lastUsedAt)}`;
  return `${made} · ${used}`;
}

/** Connected apps' explanation (g56). */
export const CONNECTIONS_INTRO =
  "Apps and assistants you've connected can read and change all your lists, including deleting items and lists. They can also see your email address, your name, the names of people who've added to your lists, and the history of your lists, including lists and items you've deleted in the last 30 days. Disconnect ends one app's access at once and leaves the others.";
export const CONNECTIONS_TOKENS = "API tokens made for scripts are separate and aren't listed here.";

export function connectionsSection(host: Host): ConnectionsSection {
  let loading = false;
  let disconnecting = false;
  let disposed = false;
  // Where the focus lands when the last connection goes: there is no Close
  // button on a page, and the heading is what the section is.
  const heading = el("h3", { class: "settings-subhead", text: "Connected apps", attrs: { tabindex: "-1" } });
  // Not a live region: the app has one, and `host.announce` speaks through it.
  const status = el("p", { class: "popup-note connections-status" });
  const rows = el("ul", { class: "connections-list", attrs: { hidden: true } });
  // `aria-disabled` rather than `disabled` while a load runs, so a keyboard
  // user who pressed it keeps their place on it.
  const retry = el("button", { class: "secondary", text: "Try again", attrs: { type: "button", hidden: true },
    on: { click: () => { if (!loading) void load(true); } } });
  const node = el("div", { class: "connections", children: [
    heading,
    // What a connection can do, in plain words (g56): every grant is the one
    // full scope, so it can delete too. Script tokens are a different thing
    // and are not listed here, which the second line says so nobody looks.
    el("p", { class: "popup-note", text: CONNECTIONS_INTRO }),
    el("p", { class: "popup-note", text: CONNECTIONS_TOKENS }),
    status, rows, retry,
  ] });

  function say(message: string, announce: boolean) {
    status.textContent = message;
    if (announce && message) host.announce(message);
  }

  function row(connection: Connection): HTMLElement {
    const name = connection.client.name;
    const made = day.format(connection.createdAt);
    const button = el("button", { class: "secondary connection-disconnect", text: "Disconnect",
      attrs: { type: "button", "aria-label": `Disconnect ${name}, connected ${made}` },
      on: { click: () => {
        if (disconnecting || loading) return;
        host.confirm({
          title: `Disconnect ${name}?`,
          message: "It can no longer read or change your lists. Other connections are unaffected.",
          keep: { label: "Keep", onChoose: () => {} },
          act: { label: "Disconnect", danger: true, onChoose: () => { void disconnect(connection, item); } },
        }, { returnFocusTo: () => (button.isConnected ? button : heading) });
      } } });
    const item = el("li", { class: "connection-row", attrs: { "data-connection": connection.id }, children: [
      el("div", { class: "connection-text", children: [
        el("p", { class: "connection-name", text: connection.client.host ? `${name} (${connection.client.host})` : name }),
        el("p", { class: "popup-note connection-meta", text: describe(connection) }),
      ] }),
      button,
    ] });
    return item;
  }

  function render(connections: Connection[], announce: boolean) {
    rows.replaceChildren(...connections.map(row));
    rows.hidden = connections.length === 0;
    say(connections.length === 0 ? "No apps are connected." : "", announce);
  }

  /**
   * The first load is page content and says nothing aloud, nor moves the
   * focus: somebody opening Settings to change the theme is not asking about
   * connections. A load they asked for, from Try again, answers them.
   */
  async function load(asked: boolean) {
    loading = true;
    retry.setAttribute("aria-disabled", "true");
    if (!asked) retry.hidden = true;
    rows.hidden = true;
    say("Loading connections…", false);
    try {
      const connections = await listConnections();
      if (disposed) return;
      const focused = retry.contains(document.activeElement);
      retry.hidden = true;
      render(connections, asked);
      if (focused) (rows.querySelector<HTMLElement>("button") ?? heading).focus({ preventScroll: true });
    } catch (error) {
      if (disposed) return;
      say(messageOf(error), asked);
      retry.hidden = false;
    } finally {
      loading = false;
      retry.removeAttribute("aria-disabled");
    }
  }

  async function disconnect(connection: Connection, item: HTMLElement) {
    disconnecting = true;
    const buttons = [...rows.querySelectorAll<HTMLButtonElement>("button")];
    for (const button of buttons) button.setAttribute("aria-disabled", "true");
    try {
      await disconnectConnection(connection.id);
      if (disposed) return;
      // Where the focus goes next: the row below, else the row above, else the heading.
      const next = (item.nextElementSibling ?? item.previousElementSibling)?.querySelector<HTMLButtonElement>("button");
      const focused = item.contains(document.activeElement);
      item.remove();
      for (const button of buttons) button.removeAttribute("aria-disabled");
      if (rows.children.length === 0) render([], false);
      host.announce(`${connection.client.name} disconnected.`);
      if (focused || document.activeElement === document.body) (next ?? heading).focus({ preventScroll: true });
    } catch (error) {
      if (disposed) return;
      for (const button of buttons) button.removeAttribute("aria-disabled");
      say(messageOf(error), true);
      item.querySelector<HTMLButtonElement>("button")?.focus({ preventScroll: true });
    } finally {
      disconnecting = false;
    }
  }

  void load(false);
  return {
    node,
    // Only a disconnect holds the page. A load is a read, and leaving mid-read
    // costs nothing; a disconnect leaving mid-request would never say whether
    // it worked, which is what g47's popup refused to let happen too.
    busy: () => disconnecting,
    dispose() { disposed = true; },
  };
}
